/*
 * Documentation:
 * Reports page — https://app.subframe.com/library?component=Reports+page_1b2073e5-8ee6-4c66-a27b-68b29a0a2dca
 * Select — https://app.subframe.com/library?component=Select_bb88f90b-8c43-4b73-9c2f-3558ce7838f3
 * Page Header — https://app.subframe.com/library?component=Page+Header_4563daa8-568e-4a98-924a-0d956da3e7ec
 * Custom Stat Card — https://app.subframe.com/library?component=Custom+Stat+Card_ec6b5f2a-cbac-4a61-abfc-669880be3c72
 * Icon with background — https://app.subframe.com/library?component=Icon+with+background_c5d68c0e-4c0c-4cff-8d8c-6ff334859b3a
 * Checkbox Card — https://app.subframe.com/library?component=Checkbox+Card_de0b4dfb-3946-4702-be52-5678dd71925a
 * Tooltip — https://app.subframe.com/library?component=Tooltip_ccebd1e9-f6ac-4737-8376-0dfacd90c9f3
 * Table — https://app.subframe.com/library?component=Table_142dfde7-d0cc-48a1-a04c-a08ab2252633
 * Area Chart — https://app.subframe.com/library?component=Area+Chart_8aa1e7b3-5db6-4a62-aa49-137ced21a231
 */

import dayjs from "dayjs";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";
import { Skeleton, Snackbar } from "@mui/material";
import React, { useEffect, useState } from "react";

import {
  Bar,
  XAxis,
  Legend,
  Tooltip,
  BarChart,
  LabelList,
  ResponsiveContainer,
} from "recharts";

import useUser from "hooks/userHooks";

import * as SubframeCore from "@subframe/core";
import { Table } from "subframe/components/Table";
import { Select } from "subframe/components/Select";
import { PageHeader } from "subframe/components/PageHeader";
import { CustomStatCard } from "subframe/components/CustomStatCard";

import {
  fetchTrendsOverTime,
  fetchReportsHighlights,
  fetchEmployeeStatsByRegion,
} from "modules/Retention/services/retention";
import { REPORTS_DATE_FILTER_OPTIONS } from "modules/Retention/constants/constants";

import { ERROR_CODE } from "constants/constants";

import { isNumber } from "utils/number";
import { getLastNDaysFromToday } from "utils/date";
import { isEmpty as isArrayEmpty } from "utils/array";
import { isEmpty as isObjectEmpty } from "utils/object";
import { isEmpty as isStringEmpty } from "utils/string";

import { mixpanelTrackEvent } from "services/mixpanel";
import { EVENT_TYPES } from "constants/mixpanel";

import EmptyState from "../EmptyState";

import EmbedAnalytics from "../Analytics";

import config from "../../../../config";

function Reports() {
  const DEFAULT_CHART_ITEMS = 6;

  const navigate = useNavigate();
  const defaultDateFilter = REPORTS_DATE_FILTER_OPTIONS["Last 7 days"];

  const [chartData, setChartData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedDateFilter, setSelectedDateFilter] = useState(
    defaultDateFilter.label
  );
  const [reportsHighlights, setReportsHighlights] = useState({});
  const [isFetchingTrendsOverTime, setIsFetchingTrendsOverTime] =
    useState(false);
  const [isLoadingReportsHighlights, setIsLoadingReportsHighlights] =
    useState(false);
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);
  const [employeeStatsByRegion, setEmployeeStatsByRegion] = useState({});
  const [dateFilter, setDateFilter] = useState({
    endDate: getLastNDaysFromToday(defaultDateFilter.value)?.endDate || null,
    startDate:
      getLastNDaysFromToday(defaultDateFilter.value)?.startDate || null,
  });
  const [isLoadingEmployeeStatsByRegion, setIsLoadingEmployeeStatsByRegion] =
    useState(false);

  const { user, isLoading: isLoadingUser } = useUser();

  useEffect(() => {
    handleFetchTrendsOverTime();
    mixpanelTrackEvent(EVENT_TYPES.REPORTS_PAGE_NAVIGATION);
  }, []);

  useEffect(() => {
    handleFetchReportsHighlights(dateFilter);
    handleFetchEmployeesStatsByRegion(dateFilter);
  }, [dateFilter]);

  /**
   * Method to handle the fetching of the report highlights.
   *
   * @param {Object} filter
   * @returns {Null | Undefined}
   */
  const handleFetchReportsHighlights = async (filter = {}) => {
    setIsLoadingReportsHighlights(true);
    const response = await fetchReportsHighlightsAsync(filter);

    setIsLoadingReportsHighlights(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching reports highlights: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setReportsHighlights({});

      return;
    }

    setReportsHighlights(response?.result?.reports);
  };

  /**
   * Method to fetch at report highlights by API call.
   *
   * @param {Object} filter
   * @returns {Promise}
   */
  const fetchReportsHighlightsAsync = async (filter = {}) => {
    try {
      return await fetchReportsHighlights({
        start_date: filter?.startDate,
        end_date: filter?.endDate,
      });
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Method to handle the fetching of the employee stats by region.
   *
   * @param {Object} filter
   * @returns {Null | Undefined}
   */
  const handleFetchEmployeesStatsByRegion = async (filter = {}) => {
    setIsLoadingEmployeeStatsByRegion(true);
    const response = await fetchEmployeeStatsByRegionAsync(filter);

    setIsLoadingEmployeeStatsByRegion(false);

    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching employee stats by region: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setReportsHighlights({});

      return;
    }

    const employeeStatsByRegion = response?.result?.location;
    let data = [];

    if (!isObjectEmpty(employeeStatsByRegion)) {
      data = Object.keys(employeeStatsByRegion).map((region, index) => ({
        id: index,
        region,
        hires: employeeStatsByRegion[region]?.["Total"],
        terminations: employeeStatsByRegion[region]?.["Terminated"],
        escalationResolved:
          employeeStatsByRegion[region]?.["escalation_resolved"],
        escalationTotal: employeeStatsByRegion[region]?.["escalation_total"],
        escalationResolvedRate:
          employeeStatsByRegion[region]?.["escalation_resolved_rate"],
        surveySent: employeeStatsByRegion[region]?.["Survey Sent"],
        surveyCompleted: employeeStatsByRegion[region]?.["Survey Completed"],
        surveyResponseRate: employeeStatsByRegion[region]?.["Response Rate"],
      }));
    }

    setEmployeeStatsByRegion(data);
  };

  /**
   * Method to fetch the employee stats by region by API call.
   *
   * @param {Object} filter
   * @returns {Promise}
   */
  const fetchEmployeeStatsByRegionAsync = async (filter = {}) => {
    try {
      return await fetchEmployeeStatsByRegion({
        start_date: filter?.startDate,
        end_date: filter?.endDate,
      });
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Renders the error snackbar.
   *
   * @returns {Component}
   */
  const renderErrorSnackbar = () => (
    <Snackbar
      open={isErrorMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsErrorMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsErrorMessageShown(false)}
        severity="error"
        sx={{ width: "100%" }}
      >
        {errorMessage}
      </MuiAlert>
    </Snackbar>
  );

  const handleGetLastNDaysFromToday = (n = 0) => {
    const result = getLastNDaysFromToday(n);
    const startDate =
      dayjs(result?.startDate)?.format("YYYY-MM-DD") === "Invalid Date"
        ? null
        : dayjs(result?.startDate)?.format("YYYY-MM-DD");
    const endDate =
      dayjs(result?.endDate)?.format("YYYY-MM-DD") === "Invalid Date"
        ? null
        : dayjs(result?.endDate)?.format("YYYY-MM-DD");

    setDateFilter({
      startDate,
      endDate,
    });
  };

  /**
   * Method to fetch the trends over time data.
   *
   * @returns {Promise}
   */
  const fetchTrendsOverTimeAsync = async () => {
    try {
      return await fetchTrendsOverTime({
        weeks: DEFAULT_CHART_ITEMS,
      });
    } catch (error) {
      if (error?.code === ERROR_CODE.CANCELED) {
        navigate("/");
      }

      return { hasError: true, error };
    }
  };

  /**
   * Method to handle the fetching of the trends over time chart.
   *
   * @returns {Null | Undefined}
   */
  const handleFetchTrendsOverTime = async () => {
    setIsFetchingTrendsOverTime(true);
    const response = await fetchTrendsOverTimeAsync();

    setIsFetchingTrendsOverTime(false);
    if (response?.hasError) {
      setErrorMessage(
        `Error while fetching trends over time data: ${response?.error?.message}`
      );
      setIsErrorMessageShown(true);
      setChartData([]);

      return;
    }

    const rawData = response?.result?.data;

    let processedData;

    if (isArrayEmpty(rawData)) {
      processedData = [];
    } else {
      processedData = rawData.map((data) => {
        return {
          ...data,
          dateRange: `${
            isStringEmpty(data?.["Start date"])
              ? "N/A"
              : dayjs(data?.["Start date"]).format("MMM DD")
          } - ${
            isStringEmpty(data?.["End date"])
              ? "N/A"
              : dayjs(data?.["End date"]).format("MMM DD")
          }`,
        };
      });
    }

    setChartData(processedData);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && !isArrayEmpty(payload)) {
      return (
        <div className="shadow-md bg-white p-2 rounded-sm">
          <p className="text-default-font text-subheader font-subheader my-1">
            {label}
          </p>
          <p className="text-default-font text-body font-body">
            Hires: {payload[0].value}
          </p>
          <p className="text-default-font text-body font-body">
            Termination: {payload[1].value}
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div className="w-full h-full flex-col gap-8 pt-12 pb-12 pr-16 pl-16 items-start flex bg-default-background max-w-none overflow-auto">
        <PageHeader>
          <div className="grow shrink-0 basis-0 w-full gap-4 items-center flex">
            <SubframeCore.Icon
              className="text-neutral-400 text-header font-header"
              name="FeatherBarChart2"
            />
            <span className="grow shrink-0 basis-0 w-full text-default-font text-section-header font-section-header line-clamp-1">
              Reports
            </span>
          </div>
          <Select
            className="flex-none w-auto h-9"
            placeholder="Last 7 days"
            size="Small"
            value={selectedDateFilter}
            onValueChange={(label) => {
              setSelectedDateFilter(label);
              handleGetLastNDaysFromToday(
                REPORTS_DATE_FILTER_OPTIONS[label].value
              );
            }}
          >
            {Object.keys(REPORTS_DATE_FILTER_OPTIONS).map((key) => (
              <Select.Item
                value={REPORTS_DATE_FILTER_OPTIONS[key].label}
                key={REPORTS_DATE_FILTER_OPTIONS[key].value}
              />
            ))}
          </Select>
        </PageHeader>

        <div className="w-full flex-col gap-4 items-start flex">
          <PageHeader>
            <div className="grow shrink-0 basis-0 w-full gap-4 items-center flex">
              <SubframeCore.Icon
                className="text-subtext-color text-section-header font-section-header"
                name="FeatherGauge"
              />
              <span className="grow shrink-0 basis-0 w-full text-default-font text-subheader font-subheader line-clamp-1">
                Highlights
              </span>
            </div>
          </PageHeader>
          <div className="w-full gap-4 items-center flex">
            {isLoadingReportsHighlights ? (
              <>
                <Skeleton variant="rounded" width="100%" height="85px" />
                <Skeleton variant="rounded" width="100%" height="85px" />
                <Skeleton variant="rounded" width="100%" height="85px" />
              </>
            ) : (
              <>
                <CustomStatCard
                  title="New Hires"
                  variant="resolved"
                  value={
                    !isNumber(reportsHighlights?.new_hires_count)
                      ? "-"
                      : reportsHighlights?.new_hires_count?.toString()
                  }
                />
                <CustomStatCard
                  title="Terminations"
                  variant="at-risk"
                  value={
                    !isNumber(reportsHighlights?.terminations_count)
                      ? "-"
                      : reportsHighlights?.terminations_count?.toString()
                  }
                />
                <CustomStatCard
                  title="< 90 Day Terminations"
                  variant="at-risk"
                  value={
                    !isNumber(reportsHighlights?.terminations_count_lt_90days)
                      ? "-"
                      : reportsHighlights?.terminations_count_lt_90days?.toString()
                  }
                />
              </>
            )}
          </div>
        </div>

        <div className="w-full flex-col gap-4 items-start flex">
          <PageHeader>
            <div className="grow shrink-0 basis-0 w-full gap-4 items-center flex">
              <SubframeCore.Icon
                className="text-subtext-color text-section-header font-section-header"
                name="FeatherSearch"
              />
              <span className="grow shrink-0 basis-0 w-full text-default-font text-subheader font-subheader line-clamp-1">
                Business at a Glance
              </span>
            </div>
          </PageHeader>
          <div className="w-full gap-2 items-start flex">
            {isLoadingEmployeeStatsByRegion ? (
              <Skeleton variant="rounded" width="100%" height="200px" />
            ) : isArrayEmpty(employeeStatsByRegion) ? (
              <div className="h-48 w-full">
                <EmptyState
                  icon=<SubframeCore.Icon
                    className="text-[44px] font-[100] leading-[44px] text-neutral-400"
                    name="FeatherFileBox"
                  />
                  primaryText="Nothing here yet."
                  secondaryText="We’ll update this as soon as we have the information."
                />
              </div>
            ) : (
              <div className="w-full max-h-[360px] overflow-y-auto">
                <Table
                  header={
                    <Table.HeaderRow className="sticky top-[-1px] bg-white">
                      <Table.HeaderCell>Region</Table.HeaderCell>
                      <Table.HeaderCell className="flex items-center justify-end">
                        Hires
                      </Table.HeaderCell>
                      <Table.HeaderCell className="flex items-center justify-end">
                        Terminations
                      </Table.HeaderCell>
                      <Table.HeaderCell className="flex items-center justify-end">
                        Escalations Resolved
                      </Table.HeaderCell>
                      <Table.HeaderCell className="flex items-center justify-end">
                        Survey Completion
                      </Table.HeaderCell>
                    </Table.HeaderRow>
                  }
                >
                  {employeeStatsByRegion.map((stat) => {
                    return (
                      <Table.Row>
                        <Table.Cell className="whitespace-nowrap text-body-bold font-body-bold text-neutral-700">
                          {stat?.region}
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex w-full grow shrink-0 basis-0 items-center justify-end">
                            <span className="text-default-font text-body font-body">
                              {isNumber(stat?.hires)
                                ? stat.hires.toString()
                                : ""}
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex w-full grow shrink-0 basis-0 items-center justify-end">
                            <span className="text-default-font text-body font-body">
                              {isNumber(stat?.terminations)
                                ? stat.terminations.toString()
                                : ""}
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex w-full grow shrink-0 basis-0 items-center justify-end">
                            <span className="text-default-font text-body font-body">
                              {isNumber(stat?.escalationResolved)
                                ? stat.escalationResolved.toString()
                                : "-"}
                              /
                              {isNumber(stat?.escalationTotal)
                                ? stat.escalationTotal.toString()
                                : "-"}{" "}
                              (
                              {isNumber(stat?.escalationResolvedRate)
                                ? `${stat.escalationResolvedRate.toString()}%`
                                : ""}
                              )
                            </span>
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex w-full grow shrink-0 basis-0 items-center justify-end">
                            <span className="text-default-font text-body font-body">
                              {isNumber(stat?.surveyCompleted)
                                ? stat.surveyCompleted.toString()
                                : "-"}
                              /
                              {isNumber(stat?.surveySent)
                                ? stat.surveySent.toString()
                                : "-"}{" "}
                              (
                              {isNumber(stat?.surveyResponseRate)
                                ? `${stat.surveyResponseRate.toString()}%`
                                : ""}
                              )
                            </span>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table>
              </div>
            )}
          </div>
        </div>

        <div className="w-full flex-col gap-4 items-start flex">
          <PageHeader>
            <div className="grow shrink-0 basis-0 w-full gap-4 items-center flex">
              <SubframeCore.Icon
                className="text-subtext-color text-section-header font-section-header"
                name="FeatherLineChart"
              />
              <span className="grow shrink-0 basis-0 w-full text-default-font text-subheader font-subheader line-clamp-1">
                Trends over Time
              </span>
            </div>
          </PageHeader>
          <div className="w-full gap-2 items-start flex">
            {isFetchingTrendsOverTime ? (
              <Skeleton variant="rounded" width="100%" height="400px" />
            ) : isArrayEmpty(chartData) ? (
              <div className="h-48 w-full">
                <EmptyState
                  icon=<SubframeCore.Icon
                    className="text-[44px] font-[100] leading-[44px] text-neutral-400"
                    name="FeatherFileBox"
                  />
                  primaryText="Nothing here yet."
                  secondaryText="We’ll update this as soon as we have the information."
                />
              </div>
            ) : (
              <ResponsiveContainer width="100%" minHeight={400}>
                <BarChart
                  width={500}
                  height={400}
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 20,
                    left: 20,
                    bottom: 20,
                  }}
                  barCategoryGap={60}
                  style={{
                    fontFamily: "Nunito",
                    fontSize: "14px",
                  }}
                >
                  <XAxis
                    dataKey="dateRange"
                    axisLine={{
                      stroke: "#171717",
                    }}
                    tick={{ fill: "#171717" }}
                    style={{
                      fontWeight: 400,
                    }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "#fafafa" }}
                  />
                  <Legend
                    formatter={(value, entry, index) => (
                      <span
                        className="text-default-font text-body font-body"
                        style={{
                          color: "#171717",
                        }}
                      >
                        {value}
                      </span>
                    )}
                  />
                  <Bar dataKey="Hires" fill="#bbf7d0" spacing={1}>
                    <LabelList
                      dataKey="Hires"
                      position="top"
                      style={{
                        fontWeight: 400,
                        fill: "#171717",
                      }}
                    />
                  </Bar>
                  <Bar dataKey="Termination" fill="#fecaca" spacing={1}>
                    <LabelList
                      dataKey="Termination"
                      position="top"
                      style={{
                        fontWeight: 400,
                        fill: "#171717",
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
      {!isLoadingUser && (
        <EmbedAnalytics
          clientId={config.analyticsClientId}
          embedId={config.analyticsEmbedId}
          disabled={!user?.configuration?.enable_analytics_dashboard}
        />
      )}
      {renderErrorSnackbar()}
    </>
  );
}

export default Reports;
